import { createContext, useContext, useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Store interceptor IDs for cleanup
let requestInterceptor = null
let responseInterceptor = null

// Axios interceptor for token management
const setupAxiosInterceptors = (token, logout, isLoggingOut) => {
  // Clear existing interceptors first
  if (requestInterceptor !== null) {
    axios.interceptors.request.eject(requestInterceptor)
  }
  if (responseInterceptor !== null) {
    axios.interceptors.response.eject(responseInterceptor)
  }

  // Request interceptor
  requestInterceptor = axios.interceptors.request.use(
    (config) => {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor
  responseInterceptor = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Only logout if we have a valid token and aren't already logging out
        const currentToken = localStorage.getItem('token')
        if (currentToken && !isLoggingOut) {
          logout()
          // Don't show additional toast here since logout() already shows one
        }
      }
      return Promise.reject(error)
    }
  )
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(localStorage.getItem('token'))
  const [loading, setLoading] = useState(true)
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  // Login function
  const login = async (email, password) => {
    try {
      console.log('🔐 Attempting login with:', { email, password: '***' })
      
      const response = await axios.post('/api/auth/login', {
        email,
        password
      })

      console.log('✅ Login response:', response.data)

      const { token: authToken, user: userData } = response.data
      
      setToken(authToken)
      setUser(userData)
      localStorage.setItem('token', authToken)
      
      toast.success('Login successful!')
      return { success: true }
    } catch (error) {
      console.error('❌ Login error:', error)
      console.error('❌ Login error response:', error.response?.data)
      const message = error.response?.data?.error || 'Login failed'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  // Logout function
  const logout = () => {
    // Prevent multiple logout calls
    if (isLoggingOut) return
    setIsLoggingOut(true)
    
    // Clear interceptors
    if (requestInterceptor !== null) {
      axios.interceptors.request.eject(requestInterceptor)
      requestInterceptor = null
    }
    if (responseInterceptor !== null) {
      axios.interceptors.response.eject(responseInterceptor)
      responseInterceptor = null
    }
    
    setToken(null)
    setUser(null)
    localStorage.removeItem('token')
    delete axios.defaults.headers.common['Authorization']
    
    // Only show logout toast if we're not already on the login page
    if (window.location.pathname !== '/login') {
      toast.info('Logged out successfully')
    }
    
    // Reset logout flag after a brief delay
    setTimeout(() => setIsLoggingOut(false), 1000)
  }

  // Get current user data
  const getCurrentUser = async () => {
    try {
      const response = await axios.get('/api/auth/me')
      setUser(response.data.user)
      return response.data.user
    } catch (error) {
      console.error('Failed to get current user:', error)
      logout()
      return null
    }
  }

  // Refresh token
  const refreshToken = async () => {
    try {
      const response = await axios.post('/api/auth/refresh')
      const { token: newToken } = response.data
      
      setToken(newToken)
      localStorage.setItem('token', newToken)
      
      return newToken
    } catch (error) {
      console.error('Failed to refresh token:', error)
      logout()
      return null
    }
  }

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      const response = await axios.put(`/api/users/${user.id}`, userData)
      setUser(response.data.user)
      toast.success('Profile updated successfully!')
      return { success: true, user: response.data.user }
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to update profile'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  // Check permissions
  const hasPermission = (requiredLevel) => {
    if (!user || !user.role) return false
    return user.role.level <= requiredLevel
  }

  const canApproveStage = (stage) => {
    const stagePermissions = {
      'Site Visit': 4, // BD Executive and above
      'Internal Approval': 3, // Head of Acquisition / COO approval
      'CEO Approval': 1, // CEO only
      'Proposal Sent': 3, // Head of Acquisition and above
      'Pre-Work Checklist': 3, // Head of Acquisition and above
      'Agreement Signed': 3,
      'Handed to Activation': 2, // COO and above
      // Legacy stage names (kept for backwards compatibility with older records)
      'Facility Work': 3,
      'Post Reki Approved': 2,
      'Gym Onboard': 1
    }

    const requiredLevel = stagePermissions[stage]
    return hasPermission(requiredLevel)
  }

  // Initialize auth on app load
  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        setupAxiosInterceptors(token, logout, isLoggingOut)
        await getCurrentUser()
      }
      setLoading(false)
    }

    initAuth()
  }, [token, isLoggingOut])

  // Update interceptors when token changes
  useEffect(() => {
    if (token) {
      setupAxiosInterceptors(token, logout, isLoggingOut)
    }
  }, [token, isLoggingOut])

  // Cleanup interceptors on unmount
  useEffect(() => {
    return () => {
      if (requestInterceptor !== null) {
        axios.interceptors.request.eject(requestInterceptor)
      }
      if (responseInterceptor !== null) {
        axios.interceptors.response.eject(responseInterceptor)
      }
    }
  }, [])

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    getCurrentUser,
    refreshToken,
    updateProfile,
    hasPermission,
    canApproveStage,
    // Role checks
    isCEO: user?.role?.level === 1,
    isCOO: user?.role?.level === 2,
    isHeadOfAcquisition: user?.role?.level === 3,
    isBDExecutive: user?.role?.level === 4,
    isFacilityTeam: user?.role?.level === 5
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 